/**
 * Permission codes come from the tenant DB via groups; session payload includes `permissions`
 * (Redis-backed GET /auth/context).
 * Service Admin is treated as having every catalog permission for services assigned to the tenant.
 */
import {
  PERMISSION_OPTIONS,
  resolvePermissionServiceCode,
  TENANT_ADMIN_PERMISSION,
} from 'platform/rbac/Permissions';

export { TENANT_ADMIN_PERMISSION };

const normalizeRoleToken = (value) =>
  String(value ?? '')
    .trim()
    .toUpperCase()
    .replace(/[\s_-]+/g, '');

export function isServiceAdminProfile(profile) {
  if (!profile) return false;
  if (normalizeRoleToken(profile.role) === 'SERVICEADMIN') return true;
  if (Array.isArray(profile.roles)) {
    return profile.roles.some((role) => normalizeRoleToken(role) === 'SERVICEADMIN');
  }
  return false;
}

export function profileTenantServiceCodes(profile) {
  if (!profile || !Array.isArray(profile.serviceCodes)) return [];
  return profile.serviceCodes.map((code) => String(code).trim()).filter(Boolean);
}

/** True when `code` belongs to a service this tenant is subscribed to. */
export function permissionMatchesTenantServices(profile, code) {
  const needle = String(code ?? '')
    .trim()
    .toUpperCase();
  if (!needle) return false;
  const serviceCodes = profileTenantServiceCodes(profile);
  if (!serviceCodes.length) return false;
  const option = PERMISSION_OPTIONS.find((entry) => entry.permission === needle);
  const serviceCode = option?.serviceCode || resolvePermissionServiceCode(needle);
  return Boolean(serviceCode && serviceCodes.includes(serviceCode));
}

export function sessionHasPermission(profile, code) {
  if (!profile || code == null) return false;
  const needle = String(code).trim().toUpperCase();
  if (!needle) return false;

  const list = profile.permissions;
  if (Array.isArray(list) && list.some((p) => String(p).trim().toUpperCase() === needle)) {
    return true;
  }

  // Service Admin: full access to permissions for all services assigned to the tenant.
  if (isServiceAdminProfile(profile) && permissionMatchesTenantServices(profile, needle)) {
    return true;
  }

  return false;
}
